import React, { useCallback, useEffect, useState } from 'react';
import { Fetcher } from '@this/src/util';
import { Loading } from '@this/shared/ui/feedbacks/loading/loading';
import { Button, BUTTON_COLOR } from '@this/shared/ui/inputs/button';
import { Alert } from '@this/shared/ui/feedbacks/alert';
import { Flex } from '@this/shared/ui/layout/flex';
import { reportError } from '@this/lib/bugsnag';

interface InvitationOrganization {
  id: number;
  name: string;
}

interface InvitationResponse {
  organization: InvitationOrganization;
}

interface Props {
  invitationId: string;
  baseUrl: string;
  redirectPath: string;
  canReject?: boolean;
}

type Status = 'approved' | 'rejected' | 'error' | null;

const ERROR_MESSAGE = '通信エラーが発生しました。時間をおいて再度お試しください。';

// NOTE: 承認・拒否のあとに招待メールのリンクを踏み直すと、サーバは404と利用者向けの文言を返す。
//       それを一律「通信エラー」にすると、既に処理済みであることが伝わらず再試行を促してしまう。
//       サーバが文言を返している場合はそれを表示する。
const errorMessageOf = (e: unknown): string => {
  const message = (e as { response?: { data?: { errors?: string[] } } })?.response?.data?.errors?.[0];
  return message || ERROR_MESSAGE;
};

// NOTE: このページは専用レイアウトを持たないルート直下に描画されるため、枠と余白はここで持つ
// （旧 expenses/organization_invitation.scss 相当）。
// NOTE: CoreApp の Wrapper は min-height:100vh の縦flexだが中身に flex:1 を与えていないため、
//       コンテンツが短いとフッターが直後に来て画面下部に届かない。Wrapper を変えると全ページに
//       影響するため、page_bad_request と同じくヘッダー(50px)・フッター(74px)を差し引いた
//       高さをこのページ側で確保する。
const InvitationLayout: React.FC = ({ children }) => (
  <div style={outerStyle}>
    <div style={cardStyle}>{children}</div>
  </div>
);

const outerStyle: React.CSSProperties = {
  minHeight: 'calc(100vh - 50px - 74px)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
  padding: '64px 16px'
};

const cardStyle: React.CSSProperties = {
  maxWidth: '600px',
  width: '100%',
  height: 'fit-content',
  padding: '32px',
  background: 'white',
  border: '1px solid #e0e0e0',
  borderRadius: '4px',
  fontSize: '16px',
  lineHeight: 1.8
};

export const OrganizationInvitationPage: React.FC<Props> = ({
  invitationId,
  baseUrl,
  redirectPath,
  canReject = true
}) => {
  const [organization, setOrganization] = useState<InvitationOrganization | null>(null);
  const [status, setStatus] = useState<Status>(null);
  const [errorMessage, setErrorMessage] = useState<string>(ERROR_MESSAGE);
  // NOTE: 二重送信されると、2回目はサーバ側の applied? 再チェックで404になり、承認は成功して
  //       いるのに画面がエラー表示に切り替わる。送信中はボタンを無効化する。
  const [submitting, setSubmitting] = useState(false);

  const fetchInvitation = useCallback(async () => {
    try {
      const response = await Fetcher.get<InvitationResponse>(`${baseUrl}/${invitationId}.json`);
      setOrganization(response.organization);
    } catch (e) {
      setErrorMessage(errorMessageOf(e));
      setStatus('error');
    }
  }, [baseUrl, invitationId]);

  useEffect(() => {
    fetchInvitation();
  }, [fetchInvitation]);

  const handleApprove = useCallback(async () => {
    if (submitting) return;

    setSubmitting(true);
    try {
      await Fetcher.put(`${baseUrl}/${invitationId}/approve.json`, {});
      setStatus('approved');
      setTimeout(() => {
        window.location.href = redirectPath;
      }, 1000);
    } catch (e) {
      setErrorMessage(errorMessageOf(e));
      setStatus('error');
      setSubmitting(false);
    }
  }, [baseUrl, invitationId, redirectPath, submitting]);

  const handleReject = useCallback(async () => {
    if (submitting) return;

    setSubmitting(true);
    try {
      await Fetcher.put(`${baseUrl}/${invitationId}/reject.json`, {});
      setStatus('rejected');
    } catch (e) {
      setErrorMessage(errorMessageOf(e));
      setStatus('error');
      setSubmitting(false);
    }
  }, [baseUrl, invitationId, submitting]);

  try {
    if (status === 'error') {
      return (
        <InvitationLayout>
          <Alert variant="danger">{errorMessage}</Alert>
        </InvitationLayout>
      );
    }

    if (status === 'approved') {
      return (
        <InvitationLayout>
          <p style={{ margin: '0 0 8px' }}>所属組織として追加しました。</p>
          <p style={{ margin: 0 }}>トップページへジャンプします...</p>
        </InvitationLayout>
      );
    }

    if (status === 'rejected') {
      return (
        <InvitationLayout>
          <p style={{ margin: 0 }}>招待を拒否しました。</p>
        </InvitationLayout>
      );
    }

    if (!organization) {
      return (
        <InvitationLayout>
          <Loading />
        </InvitationLayout>
      );
    }

    return (
      <InvitationLayout>
        <p style={{ margin: '0 0 8px' }}>{`${organization.name}から招待されました。`}</p>
        <p style={{ margin: '0 0 32px' }}>
          {`この招待を承認すると、${organization.name}にも所属することになります。`}
        </p>
        <Flex gap="16px" alignItems="center" justifyContent="center">
          <Button loading={submitting} onClick={handleApprove}>
            承認する
          </Button>
          {canReject && (
            <Button color={BUTTON_COLOR.DANGER} disabled={submitting} onClick={handleReject}>
              拒否する
            </Button>
          )}
        </Flex>
      </InvitationLayout>
    );
  } catch (e) {
    reportError(e);
    return null;
  }
};

export default OrganizationInvitationPage;
